/**
 * Streamline API Service
 * Handles communication with the Streamline models API
 */

class StreamlineAPI {
  constructor() {
    this.baseURL = 'https://models.streamline.xceedance.com';
    this.apiKey = 'sk-reRxnw8RVi2g1JJ99XBeuQ'; // Updated API key
    this.model = 'gpt-5-latest'; // Updated model
    this.requestQueue = [];
    this.isProcessing = false;
    this.cache = new Map();
    this.maxRetries = 3;
    this.retryDelay = 1000; // 1 second
  }

  /**
   * Initialize the API (API key is hardcoded)
   */
  async initialize() {
    try {
      // Skip health check due to CORS issues in development
      // await this.validateConnection();
      console.log('StreamlineAPI initialized (health check skipped due to CORS)');
      return { success: true, message: 'API initialized successfully' };
    } catch (error) {
      console.warn('API initialization warning:', error.message);
      return { success: false, message: error.message };
    }
  }

  /**
   * Validate API connection
   */
  async validateConnection() {
    try {
      const response = await this.makeRequest('/health', 'GET');
      if (!response.ok) {
        throw new Error('Failed to connect to Streamline API');
      }
      return true;
    } catch (error) {
      console.warn('Health check failed (likely CORS in development):', error.message);
      // Don't throw error for health check in development
      return false;
    }
  }

  /**
   * Summarize email content
   * @param {string} content - Email content to summarize
   * @param {Object} options - Summarization options
   */
  async summarizeEmail(content, options = {}) {
    const cacheKey = `You are an email summarization assistant. Your task is to summarize emails in a concise and professional manner while presenting the summary in a format that is easy to understand and digest. Use proper Markdown syntax and formatting to structure the response clearly. Ensure the tone is professional and polished, and highlight key points. The summary should include a headline or title, key takeaways, actions required (if any), and a concise summary of the content. Format your response as follows:

[Email Subject or Summary Title]
Key Takeaways:
Bullet point summary of the main points of the email.
Use short sentences to make it easy to scan and read.
Include essential details only.
Action Items/Required Actions:
List specific tasks the recipient needs to do specifically for me(if applicable).
Use a numbered list when there are multiple tasks.
Detailed Summary:
Provide a brief but coherent paragraph summarizing the email. Include relevant context, background, and any clarifications required for a complete understanding of the email.\n:${this.hashContent(content)}:${JSON.stringify(options)}`;
    
    // Check cache first
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    const payload = {
      text: content,
      task: 'summarize',
      options: {
        length: options.length || 'medium', // short, medium, long
        style: options.style || 'professional', // professional, casual, bullet-points
        focus: options.focus || 'general', // general, action-items, key-points
        ...options
      }
    };

    try {
      const response = await this.makeRequest('/v1/chat/completions', 'POST', payload);
      const result = await response.json();
      
      if (response.ok) {
        // Cache successful result
        this.cache.set(cacheKey, result);
        return result;
      } else {
        throw new Error(result.message || 'Summarization failed');
      }
    } catch (error) {
      console.error('Summarization error:', error);
      throw error;
    }
  }

  /**
   * Rephrase email content
   * @param {string} content - Email content to rephrase
   * @param {Object} options - Rephrasing options
   */
  async rephraseEmail(content, options = {}) {
    const cacheKey = `You are an email rephrasing assistant. Your role is to rewrite emails in a clear, polished, and professional manner, while adjusting the tone as requested by the user. You should preserve the original message's intent and meaning but refine the language to suit the specified tone. Structure your response to ensure the email is coherent, engaging, and tactful in all circumstances. Use proper Markdown formatting where applicable.

Instructions for Rephrasing:
Understand the Email: Fully grasp the content, intent, and purpose of the original email.
Adapt the Tone: Rewrite the email based on the specific tone requested by the user (e.g., formal, friendly, persuasive, empathetic, concise, etc.).
Maintain Professionalism: Always ensure the email conveys professionalism and clarity.
Format the Email Properly: Use standard business email structure (greeting, body, closing) and ensure clear, error-free writing.
Your Output Formatting:
If multiple tones are requested, provide separate versions for each tone, marked clearly.
Use headings in Markdown to distinguish each tone. (e.g., Formal Tone, Friendly Tone).
Ensure all versions maintain the purpose and key points of the original email.
Example Output Format:

[Requested Tone: Formal]
Example rewritten email in a formal tone.

[Requested Tone: Friendly]
Example rewritten email in a friendly tone.

[Requested Tone: Persuasive]
Example rewritten email in a persuasive tone.

Provide email rephrasing that aligns with the specified tone, with clear, professional, and polished language. If tone is not specified, default to a professional and neutral tone. Always prioritize clarity and conciseness while maintaining the original meaning.\n
:${this.hashContent(content)}:${JSON.stringify(options)}`;
    
    // Check cache first
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    const payload = {
      text: content,
      task: 'rephrase',
      options: {
        tone: options.tone || 'professional', // professional, casual, friendly, formal
        length: options.length || 'same', // shorter, same, longer
        style: options.style || 'clear', // clear, concise, detailed
        formality: options.formality || 'business', // business, personal, academic
        ...options
      }
    };

    try {
      const response = await this.makeRequest('v1/chat/completions', 'POST', payload);
      const result = await response.json();
      
      if (response.ok) {
        // Cache successful result
        this.cache.set(cacheKey, result);
        return result;
      } else {
        throw new Error(result.message || 'Rephrasing failed');
      }
    } catch (error) {
      console.error('Rephrasing error:', error);
      throw error;
    }
  }

  /**
   * Make HTTP request with retry logic
   * @param {string} endpoint - API endpoint
   * @param {string} method - HTTP method
   * @param {Object} data - Request payload
   * @param {number} retryCount - Current retry attempt
   */
  async makeRequest(endpoint, method = 'GET', data = null, retryCount = 0) {
    const url = `${this.baseURL}${endpoint}`;
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    };

    if (this.apiKey) {
      headers['Authorization'] = `Bearer ${this.apiKey}`;
    }

    const config = {
      method,
      headers,
      ...(data && { body: JSON.stringify(data) })
    };

    try {
      const response = await fetch(url, config);
      
      // Handle rate limiting
      if (response.status === 429) {
        if (retryCount < this.maxRetries) {
          const delay = this.retryDelay * Math.pow(2, retryCount); // Exponential backoff
          await this.sleep(delay);
          return this.makeRequest(endpoint, method, data, retryCount + 1);
        }
        throw new Error('Rate limit exceeded. Please try again later.');
      }

      // Handle server errors with retry
      if (response.status >= 500 && retryCount < this.maxRetries) {
        const delay = this.retryDelay * Math.pow(2, retryCount);
        await this.sleep(delay);
        return this.makeRequest(endpoint, method, data, retryCount + 1);
      }

      return response;
    } catch (error) {
      if (retryCount < this.maxRetries && this.isRetryableError(error)) {
        const delay = this.retryDelay * Math.pow(2, retryCount);
        await this.sleep(delay);
        return this.makeRequest(endpoint, method, data, retryCount + 1);
      }
      throw error;
    }
  }

  /**
   * Check if error is retryable
   * @param {Error} error - The error to check
   */
  isRetryableError(error) {
    return error.name === 'TypeError' || // Network errors
           error.message.includes('fetch') ||
           error.message.includes('network');
  }

  /**
   * Sleep utility for delays
   * @param {number} ms - Milliseconds to sleep
   */
  sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Generate cache key hash
   * @param {string} content - Content to hash
   */
  hashContent(content) {
    let hash = 0;
    for (let i = 0; i < content.length; i++) {
      const char = content.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return hash.toString();
  }

  /**
   * Clear cache
   */
  clearCache() {
    this.cache.clear();
  }

  /**
   * Generate multiple email summaries with different styles
   * @param {string} emailContent - The email content to summarize
   * @param {Object} options - Summary options
   * @returns {Promise<Array>} Array of 3-4 different summary versions
   */
  async generateEmailSummaries(emailContent, options = {}) {
    const summaryPrompts = [
      {
        style: 'Brief Overview',
        prompt: `Summarize the following email in 2-3 sentences, highlighting the main points and any action items ( for me if possible). At the end, add "KEYWORDS:" followed by a comma-separated list of the most important words/phrases that should be highlighted in the original email:\n\n${emailContent}`
      },
      {
        style: 'Key Points',
        prompt: `Extract and list the key points from this email in bullet format. At the end, add "KEYWORDS:" followed by a comma-separated list of the most important words/phrases that should be highlighted in the original email:\n\n${emailContent}`
      },
      {
        style: 'Action Items',
        prompt: `Identify and list any action items specifically for me, deadlines, or next steps mentioned in this email. If there are no action items for me, you can mention no action items. At the end, add "KEYWORDS:" followed by a comma-separated list of the most important words/phrases (especially deadlines, actions, names) that should be highlighted in the original email:\n\n${emailContent}`
      },
      {
        style: 'Executive Summary',
        prompt: `Create a professional executive summary of this email, suitable for management review. At the end, add "KEYWORDS:" followed by a comma-separated list of the most important words/phrases that should be highlighted in the original email:\n\n${emailContent}`
      }
    ];

    const summaries = [];
    
    for (const { style, prompt } of summaryPrompts) {
      try {
        const result = await this.chatCompletion({
          messages: [
            {
              role: 'system',
              content: 'You are a professional email assistant. Provide clear, concise, and accurate summaries.'
            },
            {
              role: 'user',
              content: prompt
            }
          ]
        });
        
        // Extract keywords if present
        let content = result.content;
        let keywords = [];
        
        const keywordMatch = content.match(/KEYWORDS:\s*(.+)$/im);
        if (keywordMatch) {
          keywords = keywordMatch[1].split(',').map(k => k.trim()).filter(k => k.length > 0);
          content = content.replace(/KEYWORDS:\s*(.+)$/im, '').trim();
        }
        
        summaries.push({
          style,
          content,
          keywords,
          tokens: result.usage
        });
      } catch (error) {
        console.error(`Failed to generate ${style} summary:`, error);
        summaries.push({
          style,
          content: `Error generating ${style.toLowerCase()}: ${error.message}`,
          keywords: [],
          error: true
        });
      }
    }
    
    return summaries;
  }

  /**
   * Generate multiple email rephrase versions with different tones
   * @param {string} emailContent - The email content to rephrase
   * @param {Object} options - Rephrase options
   * @returns {Promise<Array>} Array of 3-4 different rephrased versions
   */
  async generateEmailRephrases(emailContent, options = {}) {
    const rephrasePrompts = [
      {
        tone: 'Professional',
        prompt: `Rewrite the following email in a professional, business-appropriate tone while maintaining the original meaning and intent. At the end, add "KEYWORDS:" followed by a comma-separated list of the most important words/phrases that should be highlighted in the rewritten email:\n\n${emailContent}`
      },
      {
        tone: 'Friendly',
        prompt: `Rewrite the following email in a warm, friendly tone while keeping it professional. At the end, add "KEYWORDS:" followed by a comma-separated list of the most important words/phrases that should be highlighted in the rewritten email:\n\n${emailContent}`
      },
      {
        tone: 'Concise',
        prompt: `Rewrite the following email to be more concise and direct while preserving all important information. At the end, add "KEYWORDS:" followed by a comma-separated list of the most important words/phrases that should be highlighted in the rewritten email:\n\n${emailContent}`
      },
      {
        tone: 'Formal',
        prompt: `Rewrite the following email in a formal, diplomatic tone suitable for senior management or external clients. At the end, add "KEYWORDS:" followed by a comma-separated list of the most important words/phrases that should be highlighted in the rewritten email:\n\n${emailContent}`
      }
    ];

    const rephrases = [];
    
    for (const { tone, prompt } of rephrasePrompts) {
      try {
        const result = await this.chatCompletion({
          messages: [
            {
              role: 'system',
              content: 'You are a professional writing assistant. Maintain the original meaning while adjusting the tone as requested.'
            },
            {
              role: 'user',
              content: prompt
            }
          ]
        });
        
        // Extract keywords if present
        let content = result.content;
        let keywords = [];
        
        const keywordMatch = content.match(/KEYWORDS:\s*(.+)$/im);
        if (keywordMatch) {
          keywords = keywordMatch[1].split(',').map(k => k.trim()).filter(k => k.length > 0);
          content = content.replace(/KEYWORDS:\s*(.+)$/im, '').trim();
        }
        
        rephrases.push({
          tone,
          content,
          keywords,
          tokens: result.usage
        });
      } catch (error) {
        console.error(`Failed to generate ${tone} rephrase:`, error);
        rephrases.push({
          tone,
          content: `Error generating ${tone.toLowerCase()} version: ${error.message}`,
          keywords: [],
          error: true
        });
      }
    }
    
    return rephrases;
  }

  /**
   * Generate complete email from user prompt (Help me write feature)
   * @param {string} prompt - User's description of what they want to write
   * @param {Object} options - Email style and formatting options
   * @returns {Promise<Object>} Generated email with subject and body
   */
  async generateEmailFromPrompt(prompt, options = {}) {
    try {
      const { tone = 'professional', emailType = 'general', length = 'medium' } = options;
      
      const systemPrompt = `You are an expert email writing assistant. Generate a complete, professional email based on the user's prompt. The email should include:
      
1. An appropriate subject line
2. A well-structured body with proper greeting, content, and closing
3. Professional tone and formatting

Email style preferences:
- Tone: ${tone} (professional, casual, friendly, formal)
- Type: ${emailType} (general, meeting_request, follow_up, introduction, complaint, thank_you)
- Length: ${length} (short, medium, long)

Guidelines:
- Use appropriate greetings and closings
- Keep content clear and concise
- Maintain professional standards
- Include proper email formatting
- Make it ready to send

Return the response in this exact JSON format:
{
  "subject": "Email subject line here",
  "body": "Email body content here with proper formatting"
}`;

      const userPrompt = `Please write an email about: ${prompt}`;

      const response = await this.chatCompletion({
        messages: [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: userPrompt }
        ],
        max_tokens: 1000
      });

      // Parse the JSON response
      try {
        const emailData = JSON.parse(response.content);
        return {
          subject: emailData.subject || 'Generated Email',
          body: emailData.body || response.content,
          tone: tone,
          type: emailType,
          generatedAt: new Date().toISOString()
        };
      } catch (parseError) {
        // Fallback if JSON parsing fails
        console.warn('Could not parse JSON response, using raw content');
        return {
          subject: 'Generated Email',
          body: response.content,
          tone: tone,
          type: emailType,
          generatedAt: new Date().toISOString()
        };
      }
      
    } catch (error) {
      console.error('Email generation error:', error);
      throw new Error(`Failed to generate email: ${error.message}`);
    }
  }

  /**
   * Generate multiple email variations from prompt
   * @param {string} prompt - User's description
   * @param {Object} options - Email options
   * @returns {Promise<Array>} Array of email variations
   */
  async generateEmailVariations(prompt, options = {}) {
    try {
      const tones = ['professional', 'friendly', 'formal'];
      const promises = tones.map(tone => 
        this.generateEmailFromPrompt(prompt, { ...options, tone })
      );
      
      const results = await Promise.all(promises);
      return results;
    } catch (error) {
      console.error('Email variations generation error:', error);
      throw error;
    }
  }
  async chatCompletion(params) {
    const payload = {
      model: this.model,
      messages: params.messages,
      ...params
    };

    const headers = {
      'Authorization': `Bearer ${this.apiKey}`,
      'Content-Type': 'application/json'
    };

    try {
      const response = await fetch(`${this.baseURL}/v1/chat/completions`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const result = await response.json();
      
      // Extract the assistant's message just like in Python code
      if (result.choices && result.choices[0] && result.choices[0].message) {
        return {
          content: result.choices[0].message.content,
          usage: result.usage,
          raw: result
        };
      } else {
        throw new Error('Invalid response format from API');
      }
      
    } catch (error) {
      console.error('Chat completion error:', error);
      throw error;
    }
  }

  /**
   * Get API status
   */
  getStatus() {
    return {
      connected: !!this.apiKey,
      cacheSize: this.cache.size,
      queueLength: this.requestQueue.length
    };
  }
}

// Export singleton instance
export const streamlineAPI = new StreamlineAPI();
export default streamlineAPI;